import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { createLeadForm, getGeosList, getTrainingCentersByService, getServices, getFormOptions, getHoodsByCluster, getUpcomingBatches, checkLeadERExists } from "../Api/api";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

const EMPTY_FORM = {
  fullName: "",
  phoneNumber: "",
  gender: "",
  preferredLanguage: "",
  category: "",
  mm: "",
  nm: "",
  shiftPreference: "",
  shiftHours: "",
  trainingCenter: "",
  registrationDate: "",
  registrationTime: "",
  source: "",
  otherSource: "",
};

const convertTo24HourFormat = (time) => {
  if (!time) return '';
  return moment(time, 'HH:mm').format('HH:mm');
};


const LeadGenForm = () => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);

  const id = urlParams.get("id");
  const name = urlParams.get("name");
  const phone = urlParams.get("phone");
  const source = urlParams.get("source");

  const [formData, setFormData] = useState({
    ...EMPTY_FORM,
    fullName: name || "",
    phoneNumber: phone || "",
    source: source || "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [geosList, setGeosList] = useState([]);
  const [trainingCentersList, setTrainingCentersList] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [hoodsList, setHoodsList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [formOptions, setFormOptions] = useState({
    languages: [],
    sources: [],
    shift_types: [],
    shift_hour_options: [],
    time_slots: [],
  });

  const today = moment().format('YYYY-MM-DD');


  const handleCategoryChange = async (e) => {
    const serviceId = e.target.value;
    setFormData({ ...formData, category: serviceId, trainingCenter: "", registrationDate: "", registrationTime: "" });
    setTrainingCentersList([]);
    setBatchesList([]);
    if (serviceId) {
      try {
        const tcs = await getTrainingCentersByService(serviceId);
        setTrainingCentersList(tcs);
      } catch (error) {
        toast.error("Failed to fetch training centers");
      }
    }
  };

  const handleTCChange = async (e) => {
    const tcId = e.target.value;
    setFormData({ ...formData, trainingCenter: tcId, registrationDate: "", registrationTime: "" });
    setBatchesList([]);
    if (tcId && formData.category) {
      try {
        const batches = await getUpcomingBatches(tcId, formData.category);
        setBatchesList(batches);
      } catch (error) {
        toast.error("Failed to fetch upcoming batches");
      }
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const batch = batchesList.find((b) => b.start_date === selectedDate);
    const autoTime = batch ? batch.start_time.slice(0, 5) : "";
    setFormData({ ...formData, registrationDate: selectedDate, registrationTime: autoTime });
  };

  const handleMMChange = async (e) => {
    const clusterId = e.target.value;
    setFormData({ ...formData, mm: clusterId, nm: "" });
    setHoodsList([]);
    if (clusterId) {
      try {
        const hoods = await getHoodsByCluster(clusterId);
        setHoodsList(hoods);
      } catch (error) {
        toast.error("Failed to fetch neighbourhoods");
      }
    }
  };

  const checkPhoneExists = async (phone) => {
    try {
      const result = await checkLeadERExists(phone);
      if (result?.exists) {
        setErrors((prev) => ({ ...prev, phoneNumber: result?.message }));
      }
    } catch (error) {
      toast.error("Phone check failed:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "phoneNumber") {
      if (!value) {
        setErrors({ ...errors, phoneNumber: "Phone number is required." });
      } else if (!/^\d{10}$/.test(value)) {
        setErrors({ ...errors, phoneNumber: "Please enter a valid 10-digit phone number." });
      } else {
        setErrors({ ...errors, phoneNumber: "" });
        checkPhoneExists(value);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName) newErrors.fullName = "Full Name is required.";
    if (formData.fullName && formData.fullName.trim().length < 3)
      newErrors.fullName = "Full Name should have at least 3 characters.";

    if (!formData.phoneNumber || formData.phoneNumber.length !== 10)
      newErrors.phoneNumber = "Phone number must be a 10-digit number.";
    else if (errors.phoneNumber)
      newErrors.phoneNumber = errors.phoneNumber;

    if (!formData.gender) newErrors.gender = "Gender is required.";
    if (!formData.preferredLanguage) newErrors.preferredLanguage = "Preferred Language is required.";
    if (!formData.category) newErrors.category = "Category is required.";
    if (!formData.mm) newErrors.mm = "Work location (MM) is required.";
    if (!formData.nm) newErrors.nm = "NM is required.";
    if (!formData.shiftPreference) newErrors.shiftPreference = "Shift preference is required.";
    if (!formData.shiftHours)
      newErrors.shiftHours = "Shift hours is required.";
    if (!formData.trainingCenter) newErrors.trainingCenter = "Training Center is required.";
    if (!formData.registrationDate) newErrors.registrationDate = "Registration date is required.";
    if (!formData.registrationTime) newErrors.registrationTime = "Registration time is required.";
    if (!formData.source) newErrors.source = "Source is required.";
    if (formData.source === "Others" && !formData.otherSource)
      newErrors.otherSource = "Other source is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const answers = [
        { key: "GENDER", value: formData.gender },
        { key: "PREFERRED_LANGUAGE", value: formData.preferredLanguage },
        { key: "CLUSTER", value: String(formData.mm) },
        { key: "SHIFT_TYPE", value: formData.shiftPreference },
        { key: "SHIFT_HOURS", value: formData.shiftHours },
        { key: "TRAINING_CENTER", value: String(formData.trainingCenter) },
        { key: "REGISTRATION_DATE", value: formData.registrationDate },
        { key: "REGISTRATION_TIME", value: convertTo24HourFormat(formData.registrationTime) },
        {
          key: "SOURCE",
          value: formData.source === "Others" ? formData.otherSource : formData.source,
        },
      ];

      answers.push({ key: "HOOD", value: String(formData.nm) });

      const lowercaseAnswers = answers.map((answer) => ({
        key: answer.key,
        value: typeof answer.value === "string" ? answer.value.toLowerCase() : answer.value,
      }));

      const data = {
        phone: formData.phoneNumber,
        name: formData.fullName,
        details: lowercaseAnswers,
        service_id: Number(formData.category),
      };

      if (id) data["potential_lead_id"] = id;

      await createLeadForm(data);

      setFormData({ ...EMPTY_FORM });
      setHoodsList([]);
      setTrainingCentersList([]);
      setBatchesList([]);
      Swal.fire("Thank you for making a Submission");
      navigate("/lead-form");
    } catch (error) {
      console.error("Failed to save lead response", error);
      if (error?.response?.data?.errors?.length > 0 && error?.response?.data?.errors[0]?.message) {
        toast.error(error.response.data.errors[0].message);
      } else {
        toast.error("Something went wrong, Try again");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [geoRes, servicesRes, optionsRes] = await Promise.all([
          getGeosList(),
          getServices(),
          getFormOptions(),
        ]);
        const clusters = geoRes.clusters || [];
        const geoName = geoRes.name;
        setGeosList(clusters.map((c) => ({ id: c.id, name: c.name, label: `${geoName} : ${c.name}` })));
        setServicesList(servicesRes);
        setFormOptions(optionsRes);
      } catch (error) {
        console.error("Failed to fetch form data", error);
        toast.error("Failed to fetch required data");
      }
    };
    fetchData();
  }, []);

  return (
    <div className="container mx-auto p-6">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Full Name */}
        <div className="form-group">
          <label htmlFor="fullName" className="block font-medium">Full Name</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            className="w-full p-2 border rounded"
            placeholder="Enter full name"
          />
          {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName}</p>}
        </div>

        {/* Phone Number */}
        <div className="form-group">
          <label htmlFor="phoneNumber" className="block font-medium">Phone Number</label>
          <input
            type="number"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={(e) => {
              if (e.target.value >= 0 || e.target.value === "") handleInputChange(e);
            }}
            className="w-full p-2 border rounded"
            placeholder="Enter phone number"
          />
          {errors.phoneNumber && <p className="text-red-500 text-sm">{errors.phoneNumber}</p>}
        </div>

        {/* Gender */}
        <div className="form-group">
          <label className="block font-medium">Gender</label>
          <select name="gender" value={formData.gender} onChange={handleInputChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          {errors.gender && <p className="text-red-500 text-sm">{errors.gender}</p>}
        </div>

        {/* Preferred Language */}
        <div className="form-group">
          <label className="block font-medium">Preferred Language</label>
          <select name="preferredLanguage" value={formData.preferredLanguage} onChange={handleInputChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            {formOptions.languages.map((lang, i) => (
              <option key={i} value={lang}>{lang}</option>
            ))}
          </select>
          {errors.preferredLanguage && <p className="text-red-500 text-sm">{errors.preferredLanguage}</p>}
        </div>

        {/* Category */}
        <div className="form-group">
          <label className="block font-medium">Category</label>
          <select name="category" value={formData.category} onChange={handleCategoryChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            {servicesList.map((svc, i) => (
              <option key={i} value={svc.id}>{svc.name}</option>
            ))}
          </select>
          {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}
        </div>

        {/* Where do you work? (MM) */}
        <div className="form-group">
          <label className="block font-medium">Where do you work? (MM)</label>
          <select name="mm" value={formData.mm} onChange={handleMMChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            {geosList.map((geo, i) => (
              <option key={i} value={geo.id}>{geo.label}</option>
            ))}
          </select>
          {errors.mm && <p className="text-red-500 text-sm">{errors.mm}</p>}
        </div>

        {/* NM */}
        <div className="form-group">
          <label className="block font-medium">NM</label>
          <select
            name="nm"
            value={formData.nm}
            onChange={handleInputChange}
            disabled={!formData.mm}
            className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{formData.mm ? "Select" : "Select MM first"}</option>
            {hoodsList.map((hood, i) => (
              <option key={i} value={hood.id}>{hood.name}</option>
            ))}
          </select>
          {errors.nm && <p className="text-red-500 text-sm">{errors.nm}</p>}
        </div>

        {/* Source */}
        <div className="form-group">
          <label className="block font-medium">Source</label>
          <select name="source" value={formData.source} onChange={handleInputChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            {formOptions.sources.map((src, i) => (
              <option key={i} value={src}>{src}</option>
            ))}
          </select>
          {errors.source && <p className="text-red-500 text-sm">{errors.source}</p>}
        </div>

        {formData.source === "Others" && (
          <div className="form-group">
            <input
              type="text"
              id="otherSource"
              name="otherSource"
              value={formData.otherSource}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              placeholder="Enter other source"
            />
            {errors.otherSource && <p className="text-red-500 text-sm">{errors.otherSource}</p>}
          </div>
        )}

        {/* Shift Preference */}
        <div className="form-group">
          <label className="block font-medium">Shift Preference</label>
          <select name="shiftPreference" value={formData.shiftPreference} onChange={handleInputChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            {formOptions.shift_types.map((type, i) => (
              <option key={i} value={type}>{type}</option>
            ))}
          </select>
          {errors.shiftPreference && <p className="text-red-500 text-sm">{errors.shiftPreference}</p>}
        </div>

        {/* Shift Hours */}
        <div className="form-group">
          <label htmlFor="shiftHours" className="block font-medium">Shift Hours</label>
          <select id="shiftHours" name="shiftHours" value={formData.shiftHours} onChange={handleInputChange} className="w-full p-2 border rounded">
            <option value="">Select shift hours</option>
            {formOptions.shift_hour_options.map((h, i) => {
              const v = String(h).replace(/h$/i, '');
              return <option key={i} value={v}>{v}</option>;
            })}
          </select>
          {errors.shiftHours && <p className="text-red-500 text-sm">{errors.shiftHours}</p>}
        </div>

        {/* Training Center */}
        <div className="form-group">
          <label className="block font-medium">Training Center</label>
          <select
            name="trainingCenter"
            value={formData.trainingCenter}
            onChange={handleTCChange}
            disabled={!formData.category}
            className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{formData.category ? "Select" : "Select Category first"}</option>
            {trainingCentersList.map((tc, i) => (
              <option key={i} value={tc.id}>{tc.name}</option>
            ))}
          </select>
          {errors.trainingCenter && <p className="text-red-500 text-sm">{errors.trainingCenter}</p>}
        </div>

        {/* Date to visit TC for Registration */}
        <div className="form-group">
          <label className="block font-medium">Date to visit TC for Registration</label>
          <select
            name="registrationDate"
            value={formData.registrationDate}
            onChange={handleDateChange}
            disabled={!formData.trainingCenter}
            className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{formData.trainingCenter ? "Select a date" : "Select TC first"}</option>
            {batchesList.map((batch, i) => (
              <option key={i} value={batch.start_date}>{batch.start_date}</option>
            ))}
          </select>
          {errors.registrationDate && <p className="text-red-500 text-sm">{errors.registrationDate}</p>}
        </div>

        {/* Time to visit TC for Registration — auto-filled from batch start_time */}
        <div className="form-group">
          <label htmlFor="registrationTime" className="block font-medium">Time to visit TC for Registration</label>
          <input
            type="text"
            id="registrationTime"
            name="registrationTime"
            value={formData.registrationTime}
            readOnly
            placeholder="Auto-filled on date selection"
            className="w-full p-2 border rounded bg-gray-50 cursor-not-allowed"
          />
          {errors.registrationTime && <p className="text-red-500 text-sm">{errors.registrationTime}</p>}
        </div>

        <div className="form-group">
          <button
            type="submit"
            disabled={loading}
            className="w-full p-2 bg-pink-500 hover:bg-pink-600 transition-all duration-200 ease-in-out
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400
              shadow-sm hover:shadow-md active:shadow-inner text-white rounded disabled:opacity-50"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>

      </form>
    </div>
  );
};

export default LeadGenForm;
